import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { getCurrencySymbol } from '../utils/currency';
import { FaTimes, FaSave, FaCamera, FaMagic } from "react-icons/fa";
import "./css/CustomLargeCardSuper.css"; // Import the CSS file

const SUFFIX_TO_LABEL = {
  10: "1kg",
  15: "1.5kg",
  20: "2kg",
  25: "2.5kg",
  30: "3kg",
  35: "3.5kg",
  40: "4kg",
  45: "4.5kg",
  50: "5kg",
  55: "5.5kg",
  60: "6kg",
};
const LABEL_TO_SUFFIX = Object.fromEntries(
  Object.entries(SUFFIX_TO_LABEL).map(([k, v]) => [v, k])
);

const CustomLargeCardSuper = ({
  profile,
  onClose,
  onSave,
  availableFlavours,
  availableFillings,
  availableSponges,
  combinations,
  coverCombinations,
  cEvents,
  cShapes,
  cakeChargeData = {},
  personServedData = {},
  readOnly = false,
  isFromHome = false,
}) => {
  const [formData, setFormData] = useState({
    theme: "",
    cflavour: "",
    cover: "",
    cfilling: "",
    frosting: "",
    sponge: "",
    ccost: "",
    cimage: "",
  });
  const [initialFormData, setInitialFormData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const navigate = useNavigate();

  // State for filtered options, initialized as empty until data is available
  const [filteredFlavours, setFilteredFlavours] = useState([]);
  const [filteredFillings, setFilteredFillings] = useState([]);
  const [filteredSponges, setFilteredSponges] = useState([]);

  // State for cEvents checkbox selections
  const [eventInterests, setEventInterests] = useState({});
  const [initialEventInterests, setInitialEventInterests] = useState({});

  // State for cShapes checkbox selections
  const [shapeInterests, setShapeInterests] = useState({});
  const [initialShapeInterests, setInitialShapeInterests] = useState({});

  // Extract cover and frosting options from coverCombinations
  const coverOptions = Array.isArray(coverCombinations)
    ? [
        ...new Set(
          coverCombinations.map((combo) => combo.cover).filter(Boolean)
        ),
      ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    : [];
  const frostingOptions = Array.isArray(coverCombinations)
    ? [
        ...new Set(
          coverCombinations.map((combo) => combo.frosting).filter(Boolean)
        ),
      ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    : [];

  // Initialize eventInterests state based on cEvents prop and profile.cevent
  useEffect(() => {
    if (!cEvents || cEvents.length === 0) {
      setEventInterests({});
      setInitialEventInterests({});
      return;
    }

    const initialEventInterests = {};
    cEvents.forEach((event) => {
      initialEventInterests[event.name] = false;
    });

    if (profile && profile.cevent && Array.isArray(profile.cevent)) {
      profile.cevent.forEach((eventName) => {
        if (initialEventInterests.hasOwnProperty(eventName)) {
          initialEventInterests[eventName] = true;
        }
      });
    }
    setEventInterests(initialEventInterests);
    setInitialEventInterests(initialEventInterests);
  }, [cEvents, profile]);

  // Initialize shapeInterests state based on cShapes prop and profile.cshape
  useEffect(() => {
    if (!cShapes || cShapes.length === 0) {
      setShapeInterests({});
      setInitialShapeInterests({});
      return;
    }

    const initialShapeInterests = {};
    cShapes.forEach((shape) => {
      initialShapeInterests[shape.name] = false;
    });

    if (profile && profile.cshape && Array.isArray(profile.cshape)) {
      profile.cshape.forEach((shapeName) => {
        if (initialShapeInterests.hasOwnProperty(shapeName)) {
          initialShapeInterests[shapeName] = true;
        }
      });
    }

    setShapeInterests(initialShapeInterests);
    setInitialShapeInterests(initialShapeInterests);
  }, [cShapes, profile]);

  // Initialize form data based on profile
  useEffect(() => {
    if (profile) {
      const tier0 = Array.isArray(profile.tiers) && profile.tiers.length > 0 ? profile.tiers[0] : {};
      const initialData = {
        theme: profile.theme || "",
        cflavour: profile.cflavour || tier0.cflavour || "",
        cover: profile.cover || "",
        cfilling: profile.cfilling || tier0.cfilling || "",
        frosting: profile.frosting || "",
        sponge: profile.sponge || tier0.sponge || "",
        ccost: profile.ccost || "",
        cimage: profile.cimage || (profile.images && profile.images[0]) || "",
      };
      setFormData(initialData);
      setInitialFormData(initialData);
    } else {
      const initialData = {
        theme: "",
        cflavour: "",
        cover: "",
        cfilling: "",
        frosting: "",
        sponge: "",
        ccost: "",
        cimage: "",
      };
      setFormData(initialData);
      setInitialFormData(initialData);
    }
  }, [profile]);

  useEffect(() => {
    setFilteredFlavours(
      availableFlavours
        ? [...availableFlavours].sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" })
          )
        : []
    );
    setFilteredFillings(
      availableFillings
        ? [...availableFillings].sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" })
          )
        : []
    );
    setFilteredSponges(
      availableSponges
        ? [...availableSponges].sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" })
          )
        : []
    );
  }, [availableFlavours, availableFillings, availableSponges]);

  // Normalize casing of cflavour/cfilling/sponge when combinations load.
  // Home cards store values in lowercase (e.g. "butter cream") but option
  // lists use proper casing. Without this, the filter effect finds no match
  // and clears the fields.
  useEffect(() => {
    if (!profile) return;
    if (!combinations || combinations.length === 0) return;
    if (!availableFlavours || availableFlavours.length === 0) return;

    const tier0 = Array.isArray(profile.tiers) && profile.tiers.length > 0 ? profile.tiers[0] : {};
    const rawFlavour = profile.cflavour || tier0.cflavour || '';
    const rawFilling = profile.cfilling || tier0.cfilling || '';
    const rawSponge  = profile.sponge  || tier0.sponge  || '';
    if (!rawFlavour && !rawFilling && !rawSponge) return;

    const nFlavour = availableFlavours.find(
      o => o.toLowerCase() === rawFlavour.toLowerCase()
    ) || rawFlavour;

    const filteredCombos = combinations.filter(c => c.flavour === nFlavour);
    const fillings = [...new Set(filteredCombos.map(c => c.filling).filter(Boolean))];
    const sponges  = [...new Set(filteredCombos.flatMap(c => Array.isArray(c.sponge) ? c.sponge : [c.sponge]).filter(Boolean))];

    const nFilling = fillings.find(o => o.toLowerCase() === rawFilling.toLowerCase()) || rawFilling;
    const nSponge  = sponges.find(o  => o.toLowerCase() === rawSponge.toLowerCase())  || rawSponge;

    // Mark as initial so the filter effect won't clear values on next run
    isInitialRender.current = true;
    setFormData(prev => ({ ...prev, cflavour: nFlavour, cfilling: nFilling, sponge: nSponge }));
    setInitialFormData(prev => prev ? ({ ...prev, cflavour: nFlavour, cfilling: nFilling, sponge: nSponge }) : prev);
  }, [profile, combinations, availableFlavours]);

  const isInitialRender = useRef(true);

  useEffect(() => {
    const selectedFlavour = formData.cflavour;
    if (
      !isInitialRender.current &&
      (!combinations || combinations.length === 0)
    ) {
      console.warn("No combinations available for filtering.");
      return;
    }

    const filterOptions = () => {
      let filteredFlavourOptions = availableFlavours
        ? [...availableFlavours].sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" })
          )
        : [];
      let filteredFillingOptions = availableFillings
        ? [...availableFillings].sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" })
          )
        : [];
      let filteredSpongeOptions = availableSponges
        ? [...availableSponges].sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" })
          )
        : [];

      if (selectedFlavour) {
        let filteredCombinations = combinations;
        filteredCombinations = filteredCombinations.filter(
          (combo) => combo.flavour === selectedFlavour
        );
        filteredFillingOptions = [
          ...new Set(
            filteredCombinations.map((combo) => combo.filling).filter(Boolean)
          ),
        ].sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base" })
        );
        filteredSpongeOptions = [
          ...new Set(
            filteredCombinations
              .flatMap((combo) => combo.sponge)
              .filter(Boolean)
          ),
        ].sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base" })
        );

        // Only clear fields if user changed the flavour (not on initial load)
        if (!isInitialRender.current) {
          if (
            formData.cfilling &&
            !filteredFillingOptions.includes(formData.cfilling)
          ) {
            handleFieldChange("cfilling", "");
          }
          if (
            formData.sponge &&
            !filteredSpongeOptions.includes(formData.sponge)
          ) {
            handleFieldChange("sponge", "");
          }
        }

        // Auto-fill if only one option and field is empty
        if (filteredFillingOptions.length === 1 && !formData.cfilling) {
          handleFieldChange("cfilling", filteredFillingOptions[0]);
        }
        if (filteredSpongeOptions.length === 1 && !formData.sponge) {
          handleFieldChange("sponge", filteredSpongeOptions[0]);
        }
      } else {
        filteredFillingOptions = [];
        filteredSpongeOptions = [];
        if (profile && !formData.cfilling && !isInitialRender.current) {
          handleFieldChange("cfilling", "");
        }
        if (profile && !formData.sponge && !isInitialRender.current) {
          handleFieldChange("sponge", "");
        }
      }
      setFilteredFlavours(filteredFlavourOptions);
      setFilteredFillings(filteredFillingOptions);
      setFilteredSponges(filteredSpongeOptions);
    };

    filterOptions();
    isInitialRender.current = false;
  }, [
    formData,
    combinations,
    availableFlavours,
    availableFillings,
    availableSponges,
    profile,
  ]);

  const deepEqual = (obj1, obj2) => {
    if (obj1 === obj2) return true;
    if (
      typeof obj1 !== "object" ||
      obj1 === null ||
      typeof obj2 !== "object" ||
      obj2 === null
    )
      return false;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
        if (obj1[key].length !== obj2[key].length) return false;
        for (let i = 0; i < obj1[key].length; i++) {
          if (!deepEqual(obj1[key][i], obj2[key][i])) return false;
        }
      } else if (
        typeof obj1[key] === "object" &&
        typeof obj2[key] === "object"
      ) {
        if (!deepEqual(obj1[key], obj2[key])) return false;
      } else if (obj1[key] !== obj2[key]) {
        return false;
      }
    }
    return true;
  };

  const deepEqualEvents = (items1, items2) => {
    const keys1 = Object.keys(items1);
    const keys2 = Object.keys(items2);

    if (keys1.length !== keys2.length) return false;

    return keys1.every(
      (key) => keys2.includes(key) && items1[key] === items2[key]
    );
  };

  useEffect(() => {
    if (initialFormData) {
      const hasFormChanges = !deepEqual(formData, initialFormData);
      const hasEventChanges = !deepEqualEvents(
        eventInterests,
        initialEventInterests
      );
      const hasShapeChanges = !deepEqualEvents(
        shapeInterests,
        initialShapeInterests
      );
      setHasChanges(hasFormChanges || hasEventChanges || hasShapeChanges);
    }
  }, [
    formData,
    initialFormData,
    eventInterests,
    initialEventInterests,
    shapeInterests,
    initialShapeInterests,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (field === "cover") {
      const selectedCover = value;
      const coverCombo = Array.isArray(coverCombinations)
        ? coverCombinations.find((combo) => combo.cover === selectedCover)
        : null;
      const newFrosting =
        coverCombo && coverCombo.frosting ? coverCombo.frosting : "";
      setFormData((prev) => ({
        ...prev,
        frosting: newFrosting,
      }));
    }
  };

  const handleEventInterestChange = (e) => {
    const { name, checked } = e.target;
    setEventInterests((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleShapeInterestChange = (e) => {
    const { name, checked } = e.target;
    setShapeInterests((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleImageChange = (e) => {
    if (readOnly) return;

    const file = e.target.files[0];
    if (!file) return;

    const supportedFormats = ["image/jpeg", "image/png", "image/gif"];
    if (!supportedFormats.includes(file.type)) {
      setError(
        "Unsupported image format. Please upload a JPEG, PNG, or GIF image."
      );
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setError("Image size exceeds 2MB. Please upload a smaller image.");
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      if (img.width < 200 || img.height < 200) {
        setError("Recommended size 200x200 pixels.");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          cimage: reader.result,
        }));
        setError(null);
      };
      reader.readAsDataURL(file);
    };
    img.onerror = () => {
      setError("Invalid image file.");
    };
  };

  // On mount: check if ImageStudio returned a modified image (and restore saved form state)
  useEffect(() => {
    const returned = sessionStorage.getItem('studio_returned_image');
    const savedForm = sessionStorage.getItem('studio_saved_form');
    if (returned || savedForm) {
      if (savedForm) {
        const parsed = JSON.parse(savedForm);
        if (returned) parsed.cimage = returned;
        setFormData(parsed);
      } else if (returned) {
        setFormData(prev => ({ ...prev, cimage: returned }));
      }
      const savedEvents = sessionStorage.getItem('studio_saved_events');
      const savedShapes = sessionStorage.getItem('studio_saved_shapes');
      if (savedEvents) setEventInterests(JSON.parse(savedEvents));
      if (savedShapes) setShapeInterests(JSON.parse(savedShapes));
      sessionStorage.removeItem('studio_returned_image');
      sessionStorage.removeItem('studio_return_profile_id');
      sessionStorage.removeItem('studio_saved_form');
      sessionStorage.removeItem('studio_saved_events');
      sessionStorage.removeItem('studio_saved_shapes');
      setHasChanges(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ───────────────────────────────────────────────────────────────────────────

  const validateFields = () => {
    const requiredFields = [
      "cflavour",
      "cover",
      "cfilling",
      "frosting",
      "sponge",
    ];
    const emptyField = requiredFields.find(
      (field) => !formData[field] || formData[field].trim() === ""
    );
    if (emptyField) {
      return `Please fill the "${emptyField}" field.`;
    }
    if (!formData.cimage) {
      return "Image is required.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly || !hasChanges) {
      return;
    }
    const validationError = validateFields();
    if (validationError) {
      setError(validationError);
      return;
    }

    const selectedEvents = Object.keys(eventInterests).filter(
      (event) => eventInterests[event]
    );
    const selectedShapes = Object.keys(shapeInterests).filter(
      (shape) => shapeInterests[shape]
    );
    const manualCost = formData.ccost
      ? String(formData.ccost)
          .replace(/^€|^EUR/i, "")
          .trim()
      : "";
    const profileData = {
      theme: formData.theme || null,
      cover: formData.cover,
      frosting: formData.frosting,
      cflavour: formData.cflavour,
      cfilling: formData.cfilling,
      sponge: formData.sponge,
      ccost: manualCost || null,
      costest: manualCost ? null : basePriceHint || null,
      cimage: formData.cimage,
      cevent: selectedEvents,
      cshape: selectedShapes,
    };
    setError(null);
    setIsSaving(true);
    try {
      await onSave(profileData);
    } catch (err) {
      setError(`Failed to save: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const isFillingSpongeDisabled = () => {
    const selectedFlavour = formData.cflavour;
    return !selectedFlavour;
  };

  const isCoverFrostingReadOnly = () => {
    if (readOnly) return true;
    // Removed the hasTheme condition to prevent disabling the cover dropdown when theme is entered
    return false;
  };

  const imageInstructions =
    "Supported formats: JPEG, PNG, GIF. Max size: 2MB. Recommended dimensions: at least 200x200 pixels.";

  const hasTheme = !!formData.theme;

  // Sort cEvents and cShapes in ascending order
  const sortedEvents = cEvents
    ? [...cEvents].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      )
    : [];
  const sortedShapes = cShapes
    ? [...cShapes].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      )
    : [];

  // --- Dynamic pricing (base 1kg price hint for Super) ---
  const is3TierCake = Object.entries(shapeInterests).some(
    ([name, checked]) => checked && name.toLowerCase() === "3tier"
  );
  const isTierCake = Object.entries(shapeInterests).some(
    ([name, checked]) => checked && name.toLowerCase().includes("tier")
  );
  const baseSegment = is3TierCake ? "3T" : isTierCake ? "T" : "N";
  const baseSuffix = (() => {
    const nums = Object.keys(cakeChargeData)
      .filter((k) => new RegExp(`^${baseSegment}\\d+$`).test(k))
      .map((k) => parseInt(k.slice(baseSegment.length)))
      .filter((n) => !isNaN(n));
    return nums.length ? String(Math.min(...nums)) : isTierCake ? "15" : "10";
  })();

  const superComboMatch = Array.isArray(combinations)
    ? combinations.find(
        (c) =>
          c.flavour === formData.cflavour && c.filling === formData.cfilling
      ) || combinations.find((c) => c.flavour === formData.cflavour)
    : null;
  const cakePriceSegment = superComboMatch?.priceSegment === "S" ? "S" : "N";

  const superCoverMatch = Array.isArray(coverCombinations)
    ? coverCombinations.find((c) => c.cover === formData.cover)
    : null;
  const coverPriceSegment = superCoverMatch?.priceSegment === "B" ? "B" : "F";

  const ccostHasData = cakeChargeData && Object.keys(cakeChargeData).length > 0;
  const basePriceHint =
    ccostHasData && formData.cflavour
      ? (
          parseFloat(cakeChargeData[baseSegment + baseSuffix] || 0) +
          (cakePriceSegment === "S"
            ? parseFloat(cakeChargeData["S" + baseSuffix] || 0)
            : 0) +
          (!isTierCake && coverPriceSegment === "B"
            ? parseFloat(cakeChargeData["B" + baseSuffix] || 0)
            : 0)
        ).toFixed(2)
      : null;

  return (
    <div className="unified-profile-card unified-profile-user">
      {isSaving && (
        <div className="clcu-saving-bar-wrap">
          <div className="clcu-saving-bar-fill" />
        </div>
      )}
      {error && (
        <div className="error-popup">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Close</button>
        </div>
      )}
      <div className="profile-details unified-profile-user-details">
        {/* Top bar: close | status/palette | save */}
        <div className="clcs-top-bar">
          <button className="clcs-top-btn" onClick={onClose} disabled={isSaving} type="button" title="Close">
            <FaTimes />
          </button>
          {!readOnly && (
            <button className="clcs-top-btn clcs-save-btn" onClick={handleSubmit} disabled={!hasChanges || isSaving} type="button" title="Save">
              <FaSave />
            </button>
          )}
        </div>
        <div className="form-group image-section">
          <div className="profile-image-section">
            <div className="image-wrapper">
              {formData.cimage ? (
                <div
                  className="image-wrapper"
                  onClick={() =>
                    !readOnly &&
                    document.getElementById("image-upload").click()
                  }
                  style={{
                    cursor: !readOnly ? "pointer" : "default",
                  }}
                >
                  <img
                    src={formData.cimage}
                    alt="Profile Preview"
                    className="profile-image"
                  />
                </div>
              ) : (
                <div
                  className="image-placeholder"
                  onClick={() =>
                    !readOnly &&
                    document.getElementById("image-upload").click()
                  }
                >
                  {imageInstructions}
                </div>
              )}
              {!readOnly && (
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                />
              )}
              {!readOnly && (
                <input
                  id="camera-capture"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                />
              )}
              {!readOnly && (
                <button
                  type="button"
                  className="clcs-camera-btn"
                  onClick={() => document.getElementById('camera-capture').click()}
                  title="Take photo"
                >
                  <FaCamera />
                </button>
              )}
              {!readOnly && formData.cimage && (
                <button
                  type="button"
                  className="clcs-wand-btn"
                  onClick={() => {
                    sessionStorage.setItem('studio_return_profile_id', profile?.id || '');
                    sessionStorage.setItem('studio_saved_form', JSON.stringify(formData));
                    sessionStorage.setItem('studio_saved_events', JSON.stringify(eventInterests));
                    sessionStorage.setItem('studio_saved_shapes', JSON.stringify(shapeInterests));
                    navigate('/imagestudio', { state: { fromCustom: true, imageBase64: formData.cimage, profileId: profile?.id || '' } });
                  }}
                  title="Open in Image Studio"
                >
                  <FaMagic />
                </button>
              )}
            </div>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(e);
          }}
        >
          <div
            className="form-group-row-theme"
            style={{
              display: "flex",
              justifyContent: "center",
              margin: "12px 0 10px 0",
            }}
          >
            <div
              className="form-group-item"
              style={{ flex: "0 1 auto", maxWidth: "80%" }}
            >
              <div
                className="field-value"
                style={{ display: "flex", justifyContent: "center" }}
              >
                <input
                  type="text"
                  name="theme"
                  value={formData.theme}
                  onChange={handleChange}
                  placeholder="Enter theme (optional)"
                  style={{
                    fontSize: "14px",
                    border: "none",
                    textAlign: "center",
                    width: "100%",
                  }}
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>
          <div className="form-group-row">
            <div className="form-group-item">
              <label>Flavour:</label>
              <div className="field-value">
                {readOnly ? (
                  <span>{formData.cflavour}</span>
                ) : (
                  <select
                    name="cflavour"
                    value={formData.cflavour || ""}
                    onChange={(e) =>
                      handleFieldChange("cflavour", e.target.value)
                    }
                    required
                    disabled={readOnly}
                  >
                    <option value="">Select</option>
                    {filteredFlavours.map((flavour) => (
                      <option key={flavour} value={flavour}>
                        {flavour}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="form-group-item">
              <label>Cover:</label>
              <div className="field-value">
                {readOnly || isCoverFrostingReadOnly() ? (
                  <span>{formData.cover || ""}</span>
                ) : (
                  <select
                    name="cover"
                    value={formData.cover || ""}
                    onChange={(e) => handleFieldChange("cover", e.target.value)}
                    required
                    disabled={readOnly || isCoverFrostingReadOnly()}
                  >
                    <option value="">Select</option>
                    {coverOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>
          <hr className="dotted-line" style={{ margin: "-5px -5px 0 0" }} />
          <div className="form-group-row">
            <div className="form-group-item">
              <label>Filling:</label>
              <div className="field-value">
                <select
                  name="cfilling"
                  value={formData.cfilling || ""}
                  onChange={(e) =>
                    handleFieldChange("cfilling", e.target.value)
                  }
                  required
                  disabled={readOnly || isFillingSpongeDisabled()}
                >
                  <option value="">Select</option>
                  {formData.cfilling &&
                    !filteredFillings.includes(formData.cfilling) && (
                      <option value={formData.cfilling}>
                        {formData.cfilling}
                      </option>
                    )}
                  {filteredFillings.map((filling) => (
                    <option key={filling} value={filling}>
                      {filling}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group-item">
              <label>Frosting:</label>
              <div className="field-value">
                <span>{formData.frosting || ""}</span>
              </div>
            </div>
          </div>
          <hr className="dotted-line" style={{ margin: "-1px -5px 0 0" }} />
          <div className="form-group-row">
            <div className="form-group-item">
              <label>Sponge:</label>
              <div className="field-value">
                <select
                  name="sponge"
                  value={formData.sponge || ""}
                  onChange={(e) => handleFieldChange("sponge", e.target.value)}
                  required
                  disabled={readOnly || isFillingSpongeDisabled()}
                >
                  <option value="">Select</option>
                  {filteredSponges.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group-item">
              <label>Cost Est.:</label>
              <div className="field-value">
                <input
                  type="text"
                  name="ccost"
                  value={formData.ccost}
                  onChange={handleChange}
                  placeholder={basePriceHint ? `${getCurrencySymbol()}${basePriceHint}` : getCurrencySymbol()}
                  style={{
                    border: "none",
                    fontSize: "12px",
                    margin: "0 0 0 -4px",
                    width: "60px",
                  }}
                  disabled={readOnly}
                />
                {basePriceHint &&
                  personServedData[baseSegment + baseSuffix] && (
                    <span
                      style={{
                        fontSize: "10px",
                        color: "#888",
                        marginLeft: "2px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      ({personServedData[baseSegment + baseSuffix]}P)
                    </span>
                  )}
              </div>
            </div>
          </div>
          {sortedShapes.length > 0 && (
            <>
              <hr className="dotted-line" style={{ margin: "-1px -5px 0 0" }} />
              <div className="form-group-row">
                <div className="form-group-item">
                  <label>Category:</label>
                  <div
                    className="event-options"
                    style={{ display: "flex", flexWrap: "wrap", gap: "0px" }}
                  >
                    {sortedShapes.map((shape) => (
                      <label
                        key={shape.name}
                        className="event-option"
                        style={{
                          fontSize: "10px",
                          marginRight: "0px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="checkbox"
                          name={shape.name}
                          checked={shapeInterests[shape.name] || false}
                          onChange={handleShapeInterestChange}
                          disabled={readOnly}
                          style={{
                            transform: "scale(0.8)",
                            marginRight: "0px",
                          }}
                        />
                        {shape.name}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
          {sortedEvents.length > 0 && (
            <>
              <hr className="dotted-line" style={{ margin: "-1px -5px 0 0" }} />
              <div className="form-group-row">
                <div className="form-group-item">
                  <label>Events:</label>
                  <div
                    className="event-options"
                    style={{ display: "flex", flexWrap: "wrap", gap: "0px" }}
                  >
                    {sortedEvents.map((event) => (
                      <label
                        key={event.name}
                        className="event-option"
                        style={{
                          fontSize: "10px",
                          marginRight: "0px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="checkbox"
                          name={event.name}
                          checked={eventInterests[event.name] || false}
                          onChange={handleEventInterestChange}
                          disabled={readOnly}
                          style={{
                            transform: "scale(0.8)",
                            marginRight: "0px",
                          }}
                        />
                        {event.name}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default React.memo(CustomLargeCardSuper);
